import { useGetNotifications } from "@/entities/get-notifications/fetch";
import { useMultipleGetOffersCargo } from "@/entities/get-offers-cargo/fetch";
import { useReadNotification } from "@/entities/read-notification/fetch";
import { useGlobalNotification } from "@/entities/set-global-notification/fetch";
import { useAuth } from "@/shared/auth/fetch/useAuth";
import { assertNever } from "@/shared/lib/assertNever";
import type { Loadable } from "@/shared/lib/loadable";
import { loadableFromUseQueryResult } from "@/shared/lib/tanstack-query";
import { useQueryClient } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import type { FreightMatching } from "@x-mile/api-contract";
import { useCallback, useMemo, useState } from "react";

type Props = {
  isRead?: boolean;
  page: number;
  perPage: number;
};

export const useNotificationListProps = () => {
  const { setGlobalNotification } = useGlobalNotification();

  const { page, perPage } = useSearch({
    from: "/_auth_requeired/notifications/",
  });

  // 未読のみ表示するかどうか
  const [unreadOnly, setUnreadOnly] = useState<boolean>();

  const { loadableNotifications, loadableOfferCargos } =
    useNotificationListData({
      page,
      perPage,
      // NOTE: 分かりづらいが「未読のみ表示 = true」ならば「isRead = false」のお知らせを表示するという意味である
      isRead: unreadOnly ? false : undefined,
    });

  const readNotificationResult = useReadNotification();

  const queryClient = useQueryClient();

  // 1件のみ既読にする
  // トーストなし
  const readNotification = useCallback(
    async (id: number) => {
      await readNotificationResult.mutateAsync({
        data: {
          notificationIds: [id],
        },
      });

      await queryClient.invalidateQueries();
    },
    [readNotificationResult, queryClient],
  );

  // すべて既読にする
  // トーストあり
  const readAllNotifications = useCallback(
    async (ids: number[]) => {
      await readNotificationResult.mutateAsync({
        data: {
          notificationIds: ids,
        },
      });

      await queryClient.invalidateQueries();

      setGlobalNotification({
        message: "すべて既読にしました",
        variant: "success",
      });
    },
    [readNotificationResult, queryClient, setGlobalNotification],
  );

  return {
    page,
    perPage,
    unreadOnly,
    setUnreadOnly,
    loadableNotifications,
    loadableOfferCargos,
    readNotification,
    readAllNotifications,
  };
};

const useNotificationListData = ({
  isRead,
  page,
  perPage,
}: Props): {
  loadableNotifications: Loadable<FreightMatching.GetNotificationsQueryResult>;
  loadableOfferCargos: Record<
    number,
    Loadable<FreightMatching.GetOffersCargoQueryResult>
  >;
} => {
  const auth = useAuth();

  const notificationsResult = useGetNotifications(
    {
      isRead,
      pagination: {
        pageNumber: page,
        displayLimit: perPage,
      },
    },
    {
      enabled: auth.session.isAuthenticated,
    },
  );

  const loadableNotifications = useMemo(
    () => loadableFromUseQueryResult(notificationsResult),
    [notificationsResult],
  );

  const offerCargoIds = useMemo(() => {
    if (loadableNotifications.status !== "success") {
      return [];
    }

    return [
      // 重複を排除するために Set を使用する
      ...new Set(
        loadableNotifications.data.data.notifications.map((notification) => {
          switch (notification.payload.type) {
            case "delivery_proposal_created":
            case "delivery_proposal_rejected":
              return notification.payload.deliveryProposalId;
            case "contract_proposal_created":
            case "contract_proposal_rejected":
            case "contract_concluded_created":
            case "delivery_request_files_uploaded":
              return notification.payload.contractId;
            default:
              assertNever(notification.payload);
              throw new Error(
                `Unknown notification payload type: ${JSON.stringify(notification.payload)}`,
              );
          }
        }),
      ),
    ];
  }, [loadableNotifications]);

  const multipleOfferCargosResult = useMultipleGetOffersCargo(
    // idの配列を {id => id} のオブジェクトに変換して渡す
    Object.fromEntries(offerCargoIds.map((id) => [id, { id }])),
    {
      enabled: auth.session.isAuthenticated,
    },
  );

  const loadableOfferCargos = useMemo(
    // { id => result } のオブジェクトを { id => loadable } のオブジェクトに変換する
    () =>
      Object.fromEntries(
        offerCargoIds.map((id) => [
          id,
          loadableFromUseQueryResult(multipleOfferCargosResult[id]),
        ]),
      ),
    [offerCargoIds, multipleOfferCargosResult],
  );

  return {
    loadableNotifications,
    loadableOfferCargos,
  };
};
